/* eslint-disable camelcase */
import Cookies from 'js-cookie'
import {Buffer} from 'buffer/'
import {getInteractionUid, getLoginCase, getLang, getClientIdFromGACookie} from 'utils/stringUtils'
import {awsFetchRest, postFormData} from 'utils/asyncUtils'
import {getTheme} from 'theme'
import * as stringUtils from 'utils/stringUtils'
import {getDeviceType} from 'utils/browserUtils'
import {isDeviceTypeAllowed} from 'utils/deviceUtils'
import {getDomainByEnv, getOIDCUrl} from 'utils/envUtils'
import {NATIVE_IDENTITY_PREFIX, WEB_AUTHN_CREDENTIALS_LOCAL_STORAGE_KEY, WEB_AUTHN_ACCOUNTS_COOKIE_NAME} from 'const'
import config from 'config'

const brand = getTheme(window).brand.name.toLowerCase()

class HostedLoginService {
  static loginWithMicrosoft = async loginCase => this.loginWithIdentityProvider('microsoft', loginCase)

  static loginWithFacebook = async loginCase => this.loginWithIdentityProvider('facebook', loginCase)

  static loginWithGoogle = async loginCase => this.loginWithIdentityProvider('google', loginCase)

  static loginWithApple = async loginCase => this.loginWithIdentityProvider('apple', loginCase)

  static loginWithSwissID = async loginCase => this.loginWithIdentityProvider('swissid', loginCase)

  static checkIfBiometricLoginAvailable = async () => {
    const isPlatformAuthenticatorAvailable = await this.hasWebAuthnSupport()
    if (isPlatformAuthenticatorAvailable) {
      // check cookies
      const credentialIds = JSON.parse(Cookies.get(WEB_AUTHN_CREDENTIALS_LOCAL_STORAGE_KEY) || '[]')
      const accounts = JSON.parse(Cookies.get(WEB_AUTHN_ACCOUNTS_COOKIE_NAME) || '[]')
      if (accounts.length > 0 && credentialIds.length > 0) {
        // request IDS for a challenge
        // TODO: [Sentry] check endpoint validation 422 often
        const challengeUrl = `${config.identityServiceUrl}/webauthn/challenge`
        const {validCredentialIds, challenge} = await awsFetchRest(challengeUrl, 'POST', {credentialIds})
        if (Object.keys(validCredentialIds).length > 0) {
          return {platformAuthenticatorAvailable: true, validCredentials: validCredentialIds, challenge}
        }
      }
    }
    return {platformAuthenticatorAvailable: false, validCredentials: {}}
  }

  static loginWithBiometrics = async (allowedCredentials, challenge, loginCase) => {
    const publicKey = {
      rpId: getDomainByEnv(),
      userVerification: 'required',
      allowCredentials: allowedCredentials.map(credentialId => ({
        transports: ['internal'],
        type: 'public-key',
        // eslint-disable-next-line no-buffer-constructor
        id: new Uint8Array(new Buffer(credentialId, 'hex')),
      })),
      challenge: new TextEncoder().encode(challenge),
    }

    const {rawId, response} = await navigator.credentials.get({publicKey})
    const {ticket} = await awsFetchRest(`${config.identityServiceUrl}/webauthn/verify`, 'POST', {
      brand,
      loginCase,
      id: Buffer.from(rawId).toString('hex'),
      credentialUserId: {
        type: 'Buffer',
        data: Array.from(new Uint8Array(response.userHandle)),
      },
      signature: {
        type: 'Buffer',
        data: Array.from(new Uint8Array(response.signature)),
      },
      authenticatorData: {
        type: 'Buffer',
        data: Array.from(new Uint8Array(response.authenticatorData)),
      },
      clientDataJSON: {
        type: 'Buffer',
        data: Array.from(new Uint8Array(response.clientDataJSON)),
      },
    })

    const lang = getLang(window.location.search)
    const interactionUrl = this._getInteractionUrl()
    postFormData(interactionUrl, {ticket, lang})
  }

  static loginWithIdentityProvider = async (provider, loginCase, extraParams = {}) => {
    const interaction = getInteractionUid(window.location.search)
    const registrationChannel = getDeviceType()
    const lang = getLang(window.location.search)
    const clientId = getClientIdFromGACookie(Cookies.get('_ga'))
    const webAuthnSupport = await this.hasWebAuthnSupport()
    const params = {
      ...extraParams,
      ...(clientId && {clientId}),
      provider,
      registrationChannel,
      loginCase,
      brand,
      interaction,
      lang,
      webAuthnSupport,
    }

    postFormData(`${config.identityServiceUrl}/federated/begin`, params)
  }

  static login = async ({email, password, captchaToken, loginCase}) => {
    const loginUrl = `${config.identityServiceUrl}/native/login`
    const registrationChannel = getDeviceType()
    const lang = getLang(window.location.search)

    const {ticket, isLinked, bid} = await awsFetchRest(loginUrl, 'POST', {
      email,
      password,
      loginCase,
      registrationChannel,
      lang,
      brand,
      ...(captchaToken && {captchaToken}),
    })
    await this.finishInteraction({ticket, isLinked})

    return bid
  }

  static signup = async (data, brand, shouldShowNewsletters, source, loginCase, interactionToken) => {
    const {
      email,
      password,
      firstName,
      lastName,
      gender,
      marketing,
      marketing_20min,
      marketing_lematin,
      marketingLessentiel,
      marketingAutodesjahres,
      agreements,
      admeiraOptIn,
      captchaToken,
      newsletter,
    } = data
    const hasNewsletterData =
      typeof newsletter === 'object' && Object.keys(newsletter).length > 0 && shouldShowNewsletters
    const signupUrl = `${config.identityServiceUrl}/native/signup`
    const registrationChannel = getDeviceType()

    const lang = getLang(window.location.search)

    const {bid, ticket, isLinked} = await awsFetchRest(
      signupUrl,
      'POST',
      {
        email,
        password,
        firstName,
        lastName,
        gender,
        brand,
        registrationChannel,
        marketing,
        marketing_20min,
        marketing_lematin,
        marketingLessentiel,
        marketingAutodesjahres,
        agreements,
        source,
        loginCase,
        admeiraOptIn,
        captchaToken,
        lang,
        ...(hasNewsletterData ? {newsletter} : {}),
      },
      {
        'x-interaction-token': interactionToken,
      },
      [401, 400, 409]
    )

    localStorage.removeItem('agreements')

    return {bid, ticket, isLinked}
  }

  static finishInteraction = async ({ticket, isLinked}) => {
    const url = this._getInteractionUrl()

    const registrationChannel = getDeviceType()
    const lang = getLang(window.location.search)
    const webAuthnSupport = await this.hasWebAuthnSupport()

    postFormData(url, {
      ticket,
      isLinked,
      connection: NATIVE_IDENTITY_PREFIX,
      registrationChannel,
      lang,
      webAuthnSupport,
    })
  }

  static forgetPassword = ({email, captchaToken}) => {
    const registrationChannel = getDeviceType()
    const loginCase = getLoginCase(window.location.search)
    const lang = getLang(window.location.search)
    return awsFetchRest(`${config.identityServiceUrl}/native/password-reset/request`, 'POST', {
      email,
      registrationChannel,
      brand,
      loginCase,
      lang,
      captchaToken,
    })
  }

  static userExists = async (email, interactionToken) => {
    try {
      const userExistsUrl = `${config.identityServiceUrl}/native/emails/${email}?brand=${brand}`
      const {existsInNative, existsInLegacy, hasLegacyFieldsToMigrate, isTemporalAccount, canTemporalAccountMakeLogin} =
        await awsFetchRest(userExistsUrl, 'GET', null, {
          'x-interaction-token': interactionToken,
        })

      return {existsInNative, existsInLegacy, hasLegacyFieldsToMigrate, isTemporalAccount, canTemporalAccountMakeLogin}
    } catch (e) {
      if (e.status === 404) {
        return {
          existsInNative: false,
          existsInLegacy: false,
          hasLegacyFieldsToMigrate: false,
          isTemporalAccount: false,
          canTemporalAccountMakeLogin: false,
        }
      }
      throw e
    }
  }

  static hasWebAuthnSupport = async () => {
    if (!isDeviceTypeAllowed()) {
      return false
    }

    return window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
      ? window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      : false
  }

  static getBrandAgreements = async brand => {
    const brandAgreementsUrl = `${config.identityServiceUrl}/native/agreements/${brand}`
    try {
      const agreements = await awsFetchRest(brandAgreementsUrl, 'GET', null, {})
      return agreements
    } catch (e) {
      return {}
    }
  }

  static getNotGameBrandAgreements = async brand => {
    const allAgreements = await this.getBrandAgreements(brand)
    return allAgreements.filter(agreement => agreement.type !== 'GAME')
  }

  static getLinkingCandidateAccountInfo = async email => {
    const url = `${config.identityServiceUrl}/native/linking-candidate-account-info/${encodeURIComponent(email)}`
    try {
      const response = await awsFetchRest(url, 'GET', null, {})
      return response
    } catch (e) {
      return {}
    }
  }

  static _getInteractionUrl = () => {
    const interactionId = getInteractionUid(window.location.search)
    return `${getOIDCUrl()}/interaction/${interactionId}`
  }

  static resendSecurityResetPasswordEmail = ({brand, loginCase, email}) => {
    const lang = stringUtils.getLang(window.location.search)
    return awsFetchRest(`${process.env.REACT_APP_IDENTITY_SERVICE_URL}/native/security-password-reset/resend`, 'POST', {
      lang,
      brand,
      loginCase,
      email,
    })
  }
}

export default HostedLoginService
